<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
      </div>

      <div class="modal__main-icon modal__main-icon--accident"></div>
      <div class="modal__close" @click="closeModal()"></div>

      <div class="form">
        <div class="navigators">
          <div class="navigator navigator--active">
            <div class="navigator__bullet"></div>
            <div class="navigator__title">{{ $t(`addAccidentModal.title`) }}</div>
          </div>
        </div>
        <div class="add__form add__form--accident">
          <transition name="fade" mode="out-in">
            <Loading v-if="formLoading" />
            <div v-else class="add__content">
              <div class="accident">
                <div class="accident__row">
                  <div class="accident__column">
                    <div class="form__row">
                      <div class="form__field form__field--statuses"> 
                        <VSelect class="accident__statuses select select--default"
                          :class="{ 'select--error': accidentStatusHasError }" :options="accidentStatuses"
                          :reduce="(status) => status.id" label="name" :placeholder="$t(`addAccidentModal.${'status'}`)"
                          v-model="accidentStatusId" />
                        <div class="form__error" v-if="accidentStatusHasError">
                          {{ $t(`addAccidentModal.statusErrorMsg`) }}
                        </div>
                      </div>

                      <div class="form__field"> 
                        <input type="text" class="input input--default" :class="{ 'input--error': priceHasError }"
                          :placeholder="$t(`addAccidentModal.${'damages'}`) + ' .- CHF'" v-model="price" />
                        <div class="form__error" v-if="priceHasError">
                          {{ priceErrorPlaceholder }}
                        </div>
                      </div>
                    </div>

                    <div class="form__field"> 
                      <textarea class="accident__textarea input input--default"
                        :class="{ 'input--error': commentHasError }" cols="30" rows="10" placeholder="Text"
                        v-model="comment"></textarea>
                      <div class="form__error" v-if="commentHasError">
                        {{ $t(`addAccidentModal.commentErrorMsg`) }}
                      </div>
                    </div>
                  </div>

                  <div class="accident__column accident__column--dropfile">
                    <DropFile v-model="files" />
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
      <div v-if="isRequesting" class="modal__button modal__button--submit button button--default button--loading"></div>
      <button v-else-if="canSubmitAccident" class="modal__button modal__button--submit button button--default" @click="createAccident()">
        <span>{{ $t(`addAccidentModal.saveBtn`) }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import DropFile from "@/components/DropFile";
import Loading from "@/partials/Loading";
import statics from "@/statics";

// Global Composition Instances
const store = useStore();
const route = useRoute();

// Reactive State Hooks
const isRequesting = ref(false);
const formLoading = ref(false);

const parentId = ref(null);
const accidentId = ref("");
const policyId = ref("");
const accidentStatusId = ref("");
const price = ref("");
const comment = ref("");
const files = ref([]);

const priceHasError = ref(false);
const accidentStatusHasError = ref(false);
const commentHasError = ref(false);
const priceErrorPlaceholder = ref("");

// Vuex Computed Getters mapping 
const accidentStatuses = computed(() => store.getters.accidentStatuses);
const accidentPolicyId = computed(() => store.getters.accidentPolicyId);
const accidentToEdit = computed(() => store.getters.accidentToEdit);
const accidentParentId = computed(() => store.getters.accidentParentId);
const customer = computed(() => store.getters.customer || {});
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const policyTeamId = computed(() => {
  const policyId = Number(accidentPolicyId.value);
  if (!policyId || Number.isNaN(policyId)) {
    return Number(customer.value?.team_id ?? customer.value?.team?.id ?? null) || null;
  }

  const matchingPolicy = (customer.value?.policies || []).find(
    (policy) => Number(policy.id) === policyId
  );

  return Number(
    matchingPolicy?.team_id
      ?? matchingPolicy?.team?.id
      ?? accidentToEdit.value?.policy?.team_id
      ?? accidentToEdit.value?.policy?.team?.id
      ?? customer.value?.team_id
      ?? customer.value?.team?.id
      ?? null
  ) || null;
});
const canSubmitAccident = computed(() => {
  if (isSuperAdmin.value) return true;

  const teamId = policyTeamId.value;
  if (!teamId || teamId <= 0) return false;

  const action = Object.keys(accidentToEdit.value || {}).length ? "policy_accident_edit" : "policy_accident_create";
  return store.getters.hasTeamPermission(action, teamId);
});

// Field Validation logic blocks
const validateAccidentStatusId = () => {
  accidentStatusHasError.value = !accidentStatusId.value;
};

const validatePrice = () => {
  if (statics.stringRegex.test(price.value) || statics.symbolRegex.test(price.value)) {
    priceHasError.value = true;
    priceErrorPlaceholder.value = "Invalid price";
  } else {
    priceHasError.value = false;
    priceErrorPlaceholder.value = "";
  }
};

const validateComment = () => {
  commentHasError.value = !comment.value;
};

// Reactive Input Change Watchers
watch(accidentStatusId, () => {
  validateAccidentStatusId();
});

watch(price, () => {
  validatePrice();
});

watch(comment, () => {
  validateComment();
});

// Structural Editing Populating Context
const loadFieldsToEdit = () => {
  const {
    id,
    policy_id,
    accident_status: { id: statusId },
    price: savedPrice,
    comment: savedComment,
  } = accidentToEdit.value;

  accidentId.value = id;
  policyId.value = policy_id;
  accidentStatusId.value = statusId;
  comment.value = savedComment;
  if (savedPrice) {
    price.value = savedPrice;
  }
};

// Internal Modal State Cleanup
const closeModal = () => {
  store.commit("setShowAddAccidentModal", false);
  store.commit("setAccidentToEdit", {});
  store.commit("setAccidentParentId", null);
};

// Component Data Initialization Context (replaces created/onMounted)
onMounted(async () => {
  formLoading.value = true;

  // Emulating the role layout execution check hooks helper mapping

  await store.dispatch("getAccidentStatuses");
  policyId.value = accidentPolicyId.value;

  if (Object.keys(accidentToEdit.value).length) {
    loadFieldsToEdit();
  } else if (accidentParentId.value) {
    parentId.value = accidentParentId.value;
  }

  formLoading.value = false;
});

// Lifecycle safe auto cleanup mapping unmounted
onBeforeUnmount(() => {
  closeModal();
});

// Submission Data Action Engine Handler 
const createAccident = async () => {
  if (!canSubmitAccident.value) return;

  validateAccidentStatusId();
  validatePrice();
  validateComment();

  if (
    !accidentStatusHasError.value &&
    !priceHasError.value &&
    !commentHasError.value
  ) {
    isRequesting.value = true;
    try {
      if (Object.keys(accidentToEdit.value).length) {
        await store.dispatch("updateAccident", {
          accidentId: accidentId.value,
          policy_id: policyId.value,
          accident_status_id: accidentStatusId.value,
          price: price.value,
          comment: comment.value,
        });
      } else {
        const { data } = await store.dispatch("createAccident", {
          policy_id: policyId.value,
          accident_status_id: accidentStatusId.value,
          price: price.value,
          comment: comment.value,
          parent_id: parentId.value,
        });

        const newAccidentId = data?.data?.id;

        if (files.value.length && newAccidentId) {
          await store.dispatch("addAccidentFile", {
            accidentId: newAccidentId,
            files: files.value.map((file) => file.file),
          });
        }
      }

      const { id } = route.params;
      await store.dispatch("getGlobalCustomer", id);
      store.commit("setShowAddAccidentModal", false);
    } catch (err) {
      console.error(err);
    } finally {
      isRequesting.value = false;
    }
  }
};
</script>
<style lang="scss" scoped>
.accident {
  &__row {
    display: flex;

    .form__field {
      flex: 1;

      &--statuses {
        margin-right: 10px;
      }
    }
  }

  &__column {
    flex: 2;

    &--dropfile {
      flex: 1;
      margin-left: 10px;
    }
  }

  &__textarea {
    min-height: 103px;
    max-width: 100%;
    padding: 10px;
  }
}
</style>
